import React, { useState } from 'react';
import {
  rollCounts,
  rollLines,
  type RollLine,
  type RunSummary,
} from '@root/lib';
import { useImageUrl } from '@shared/components/ImageContext';
import { TABLE_MIN_WIDTH, useWideEnough } from '@shared/history';
import { formatMoney } from '@shared/lib';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import RollAuditDialog from './RollAuditDialog';
import { runResultLabel, signedMoney } from './runLabels';
import styles from './RunList.module.scss';

interface RoundDetailDialogProps {
  run: RunSummary | null;
  symbol: string;
  onClose: () => void;
}

const COLUMNS: [string, string][] = [
  ['Roll', 'col_roll'],
  ['Die', 'col_die'],
  ['Movement', 'col_move'],
  ['Charge', 'col_charge'],
  ['Reward', 'col_reward'],
  ['Event', 'col_event'],
];

function eventLabel(line: RollLine, t: (key: string) => string): string {
  switch (line.outcome) {
    case 'FREE_ROLL':
      return t('Free roll earned');
    case 'MULTIPLIER':
      return line.multiplier
        ? `${line.multiplier}X ${t('collected')}`
        : t('Multiplier collected');
    case 'OVERSHOOT':
      return t('Overshoot');
    case 'FINISH':
      return t('Finish 6X');
    case 'SKULL':
      return t('Skull');
    default:
      return t('Normal tile');
  }
}

const RoundDetailDialog: React.FC<RoundDetailDialogProps> = ({
  run,
  symbol,
  onClose,
}) => {
  const { t } = useTranslation();
  const { img, imgProps } = useImageUrl();
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  const [audited, setAudited] = useState<RollLine | null>(null);
  const asTable = useWideEnough(body, TABLE_MIN_WIDTH);

  if (!run) return null;

  const money = (value: number): string => formatMoney(symbol, value);
  const lines = rollLines(run);
  const { paid, free } = rollCounts(lines);
  const autos = lines.filter((line) => line.auto).map((line) => line.number);

  return (
    <div className={styles.filterScrim}>
      <div
        className={cn(styles.filterDialog, styles.detailDialog)}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.filterHead}>
          <h2 className={styles.filterTitle}>{t('Round details')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={onClose}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        <p className={styles.summary}>
          {run.roundLabel} · {runResultLabel(run.result, t)} ·{' '}
          {new Date(run.startedAt).toLocaleString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
        <p className={styles.summary}>
          {t('Roll amount {{amount}} · {{paid}} paid + {{free}} free rolls', {
            amount: money(run.rollAmount),
            paid,
            free,
          })}
        </p>
        <p className={styles.summaryStrong}>
          {t('Spent {{spent}}  ·  Total win {{win}}  ·  Net {{net}}', {
            spent: money(run.totalSpent),
            win: money(run.totalWin),
            net: signedMoney(money(Math.abs(run.net)), run.net),
          })}
        </p>

        <div className={styles.detailBody} ref={setBody}>
          {asTable && (
            <div className={styles.rollColumns}>
              {COLUMNS.map(([label, cls]) => (
                <span key={label} className={cn(styles.rollCell, styles[cls])}>
                  {t(label)}
                </span>
              ))}
            </div>
          )}

          {lines.map((line) =>
            asTable ? (
              <button
                type="button"
                className={styles.rollRow}
                key={line.number}
                onClick={() => setAudited(line)}
              >
                <span className={cn(styles.rollCell, styles.col_roll)}>
                  {line.number}
                </span>
                <span className={cn(styles.rollCell, styles.col_die)}>
                  {line.dice}
                </span>
                <span className={cn(styles.rollCell, styles.col_move)}>
                  {line.from} → {line.to}
                </span>
                <span className={cn(styles.rollCell, styles.col_charge)}>
                  {money(line.charge)}
                </span>
                <span className={cn(styles.rollCell, styles.col_reward)}>
                  {money(line.reward)}
                </span>
                <span className={cn(styles.rollCell, styles.col_event)}>
                  {eventLabel(line, t)}
                </span>
              </button>
            ) : (
              <button
                type="button"
                className={styles.rollCardRow}
                key={line.number}
                onClick={() => setAudited(line)}
              >
                <span className={styles.rollCardLine}>
                  #{line.number} · {t('Die')} {line.dice} · {line.from} →{' '}
                  {line.to} · {eventLabel(line, t)}
                </span>
                <span className={styles.rollCardLine}>
                  {t('Charged {{charge}}  /  Reward {{reward}}', {
                    charge: money(line.charge),
                    reward: money(line.reward),
                  })}
                </span>
              </button>
            )
          )}
        </div>

        <RollAuditDialog
          roll={audited}
          symbol={symbol}
          onClose={() => setAudited(null)}
        />

        <p className={styles.summary}>
          {autos.length === 0
            ? t('All rolls: manual')
            : t('Roll {{rolls}}: automatic · Other rolls: manual', {
                rolls: autos.join(', '),
              })}
        </p>

        <button
          type="button"
          className={cn(styles.apply, styles.tab_off)}
          style={
            {
              '--button-skin': `url(${img('ButtonSkin_Secondary')})`,
            } as React.CSSProperties
          }
          onClick={onClose}
        >
          {t('BACK TO HISTORY')}
        </button>
      </div>
    </div>
  );
};

export default RoundDetailDialog;
